import { Button, Tooltip } from 'antd';
import styled from 'styled-components';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useHistory, useLocation } from 'react-router-dom';
import posthog from 'posthog-js';
import { orderBy } from 'lodash';
import {
    LogoutOutlined,
    CloudUploadOutlined,
    CheckCircleOutlined,
    BarChartOutlined,
    DownloadOutlined,
    PlusCircleOutlined,
    EditOutlined,
    ImportOutlined,
    SendOutlined,
    UserOutlined,
    TeamOutlined,
} from '@ant-design/icons';
import { getMenuRoutes } from 'common/helpers/getMenuRoutes';
import {
    getOrganisationCurrentSubscription,
    isAdmin,
    isTenant,
} from 'features/user/selectors/userSelectors';
import { SENTRY_ENVIRONMENT } from 'config/env';
import { logOutRequest } from 'features/auth/actions/authActions';
import { NativeButtonProps } from 'antd/lib/button/button';
import React from 'react';
import { Notification, showNotification } from 'common/helpers/notifications';
import { EditPaths, SupplierEditPaths } from 'features/edit/models/editModel';

const Nav = styled.nav`
    padding: 0 14px;
    flex: 1;
`;

const NavItem = styled(Link)<{ active?: number; $collapsed?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: ${(p) => (p.$collapsed ? 'center' : 'flex-start')};
    gap: ${(p) => (p.$collapsed ? '0' : '12px')};
    padding: ${(p) => (p.$collapsed ? '0' : '0 16px')};
    height: 44px;
    border-radius: 10px;
    font-size: 0.9rem;
    font-family: 'Inter', sans-serif;
    color: ${(p) => (p.active ? '#fff' : 'rgba(255, 255, 255, 0.55)')};
    background: ${(p) => (p.active ? 'rgba(30, 122, 69, 0.22)' : 'transparent')};
    font-weight: ${(p) => (p.active ? '600' : '400')};
    text-decoration: none;
    transition: all 0.15s ease;
    margin-bottom: 2px;
    white-space: nowrap;
    overflow: hidden;

    .anticon {
        font-size: 17px;
        color: ${(p) => (p.active ? '#a8c9a8' : 'rgba(255, 255, 255, 0.35)')};
        transition: color 0.15s ease;
    }

    .nav-label {
        opacity: ${(p) => (p.$collapsed ? 0 : 1)};
        max-width: ${(p) => (p.$collapsed ? '0' : '160px')};
        transition: opacity 0.18s ease, max-width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
        overflow: hidden;
    }

    &:hover {
        color: #fff;
        background: rgba(255, 255, 255, 0.07);
        text-decoration: none;

        .anticon {
            color: rgba(255, 255, 255, 0.7);
        }
    }
`;

const Spacer = styled.div`
    flex: 1;
`;

const BottomSection = styled.div`
    padding: 0 12px 16px;
    border-top: 1px solid rgba(255, 255, 255, 0.06);
    margin-top: 8px;
    padding-top: 12px;
`;

const LogoutBtn = styled.div<{ $collapsed?: boolean }>`
    display: flex;
    align-items: center;
    justify-content: ${(p) => (p.$collapsed ? 'center' : 'flex-start')};
    gap: ${(p) => (p.$collapsed ? '0' : '12px')};
    padding: ${(p) => (p.$collapsed ? '0' : '0 14px')};
    height: 42px;
    border-radius: 8px;
    font-size: 0.88rem;
    font-family: 'Inter', sans-serif;
    color: rgba(255, 255, 255, 0.35);
    cursor: pointer;
    transition: all 0.15s ease;
    white-space: nowrap;
    overflow: hidden;

    .anticon {
        font-size: 16px;
    }

    .logout-label {
        opacity: ${(p) => (p.$collapsed ? 0 : 1)};
        max-width: ${(p) => (p.$collapsed ? '0' : '160px')};
        transition: opacity 0.18s ease, max-width 0.22s cubic-bezier(0.4, 0, 0.2, 1);
        overflow: hidden;
    }

    &:hover {
        color: #f87171;
        background: rgba(248, 113, 113, 0.08);
    }
`;

const UpgradeBtn = styled((props: NativeButtonProps) => <Button {...props} />)`
    && {
        display: block;
        width: 100%;
        margin-top: 12px;
        border-radius: 8px;
        font-family: 'Inter', sans-serif;
        font-size: 0.82rem;
        height: 38px;
    }
`;

type Props = {
    onLinkClick?: () => void;
    collapsed?: boolean;
};

const menuIcons: Record<string, React.ReactNode> = {
    upload: <CloudUploadOutlined />,
    check: <CheckCircleOutlined />,
    dashboard: <BarChartOutlined />,
    download: <DownloadOutlined />,
    create: <PlusCircleOutlined />,
    edit: <EditOutlined />,
    import: <ImportOutlined />,
    invite: <SendOutlined />,
    assign: <TeamOutlined />,
    user: <UserOutlined />,
};

export const MenuContent = ({ onLinkClick, collapsed = false }: Props) => {
    const dispatch = useDispatch();
    const location = useLocation();
    const history = useHistory();
    const isUserAdmin = useSelector(isAdmin);
    const isUserTenant = useSelector(isTenant);
    const subscription = useSelector(getOrganisationCurrentSubscription);

    const isExpired = subscription?.status === 'EXPIRED';
    const menuRoutes = getMenuRoutes(isUserTenant, isUserAdmin, isExpired);
    const routePrefix = location.pathname.split('/')[1];

    const hasShownExpiredNotification = React.useRef(false);

    const handleLogout = () => {
        if (SENTRY_ENVIRONMENT === 'frontend-production') {
            posthog.reset(true);
        }
        dispatch(logOutRequest());
        if (isExpired) {
            history.push('/sign-in');
        }
    };

    const redirectToRenewSubscriptionView = () => {
        if (isUserTenant) {
            history.push(`${EditPaths.Subscriptions}`);
            return;
        }
        history.push(`${SupplierEditPaths.Users}`);
    };

    React.useEffect(() => {
        if (isExpired && !hasShownExpiredNotification.current) {
            hasShownExpiredNotification.current = true;
            showNotification({
                type: Notification.Warning,
                duration: 100000,
                text: 'Your subscription has expired. Please renew it to have access to all features.',
            });
        }
    }, []);

    return (
        <>
            <Nav>
                {orderBy(menuRoutes, (route) => route.order).map((route) => {
                    const item = (
                        <NavItem
                            key={route.key}
                            to={route.path}
                            onClick={onLinkClick}
                            active={routePrefix === route.key ? 1 : 0}
                            $collapsed={collapsed}
                        >
                            {menuIcons[route.key] || null}
                            <span className="nav-label">{route.label}</span>
                        </NavItem>
                    );
                    return collapsed ? (
                        <Tooltip key={route.key} title={route.label} placement="right">
                            {item}
                        </Tooltip>
                    ) : (
                        item
                    );
                })}
            </Nav>
            <BottomSection>
                {collapsed ? (
                    <Tooltip title="Logout" placement="right">
                        <LogoutBtn onClick={handleLogout} $collapsed>
                            <LogoutOutlined />
                            <span className="logout-label">Logout</span>
                        </LogoutBtn>
                    </Tooltip>
                ) : (
                    <LogoutBtn onClick={handleLogout}>
                        <LogoutOutlined />
                        <span className="logout-label">Logout</span>
                    </LogoutBtn>
                )}
                {isUserAdmin && isExpired && !collapsed && (
                    <UpgradeBtn type="default" onClick={() => redirectToRenewSubscriptionView()}>
                        Upgrade Account
                    </UpgradeBtn>
                )}
            </BottomSection>
        </>
    );
};
